import React from 'react';
import { motion } from 'framer-motion';
import { MoreHorizontal, Clock, CheckCircle2, XCircle, Package, Sparkles } from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { formatZAR } from '../../utils/currency';
import { Link } from 'react-router-dom';
const statusStyles = {
  Pending: 'bg-yellow-50 text-yellow-600',
  'Order Accepted': 'bg-blue-50 text-blue-600',
  Crafting: 'bg-indigo-50 text-indigo-600',
  'Crafting Complete': 'bg-purple-50 text-purple-600',
  'Preparing Delivery': 'bg-violet-50 text-violet-600',
  'Collection Ready': 'bg-teal-50 text-teal-600',
  'On the Way': 'bg-cyan-50 text-cyan-600',
  Collected: 'bg-green-50 text-green-600',
  Delivered: 'bg-emerald-50 text-emerald-600',
  Cancelled: 'bg-red-50 text-red-600',
  'Pending Approval': 'bg-orange-50 text-orange-600'
};
const StatusIcon = ({
  status
}: {
  status: string;
}) => {
  switch (status) {
    case 'Delivered':
    case 'Collected':
      return <CheckCircle2 size={14} className="mr-1" data-id="element-383" />;
    case 'Cancelled':
      return <XCircle size={14} className="mr-1" data-id="element-384" />;
    case 'Pending Approval':
      return <Sparkles size={14} className="mr-1" data-id="element-385" />;
    default:
      return <Clock size={14} className="mr-1" data-id="element-386" />;
  }
};
export function RecentOrders() {
  const {
    orders,
    customOrderRequests
  } = useData();
  const {
    user
  } = useAuth();
  // Convert custom order requests to order-like format for display
  const customOrdersAsOrders = customOrderRequests.filter(req => req.status === 'pending' || req.status === 'quoted').map(req => ({
    id: req.id,
    userId: req.userId,
    customerName: req.customerName,
    userRegion: undefined,
    items: [{
      productId: 'custom',
      quantity: 1,
      price: req.quotedPrice || 0,
      name: `Custom ${req.category.charAt(0).toUpperCase() + req.category.slice(1)}`
    }],
    status: 'Pending Approval' as const,
    total: req.quotedPrice || 0,
    date: req.createdAt,
    deliveryType: 'delivery' as const,
    deliveryDate: '',
    preferredTime: '',
    deliveryFee: 0,
    isCustomOrder: true
  }));
  // Combine regular orders and custom orders, sort by date
  const allOrders = [...orders, ...customOrdersAsOrders].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  const recentOrders = allOrders.slice(0, 5);
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5,
    delay: 0.3
  }} className="bg-white rounded-2xl border border-rose-100 shadow-sm overflow-hidden" data-id="element-387">
      <div className="p-6 border-b border-rose-100 flex justify-between items-center" data-id="element-388">
        <div data-id="element-389">
          <h3 className="text-lg font-bold text-gray-900 font-serif" data-id="element-390">
            Recent Orders
          </h3>
          <p className="text-xs text-gray-500 mt-1" data-id="element-391">All regions</p>
        </div>
        <Link to="/admin/orders" className="text-sm text-rose-500 font-medium hover:text-rose-600" data-id="element-392">
          View All
        </Link>
      </div>

      <div className="overflow-x-auto" data-id="element-393">
        {recentOrders.length === 0 ? <div className="px-6 py-12 text-center text-gray-500" data-id="element-394">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-3" data-id="element-395" />
            <p className="font-medium" data-id="element-396">No orders yet</p>
            <p className="text-sm mt-1" data-id="element-397">
              Orders will appear here once customers place them
            </p>
          </div> : <table className="w-full" data-id="element-398">
            <thead className="bg-rose-50/50" data-id="element-399">
              <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-400">
                <th className="px-6 py-4" data-id="element-401">Order ID</th>
                <th className="px-6 py-4" data-id="element-402">Customer</th>
                <th className="px-6 py-4" data-id="element-403">Region</th>
                <th className="px-6 py-4" data-id="element-404">Items</th>
                <th className="px-6 py-4" data-id="element-405">Status</th>
                <th className="px-6 py-4" data-id="element-406">Total</th>
                <th className="px-6 py-4" data-id="element-407"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-rose-50" data-id="element-408">
              {recentOrders.map((order, index) => <motion.tr key={order.id} initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.4 + index * 0.1
          }} className="hover:bg-rose-50/30 transition-colors group cursor-pointer" data-id="element-409">
                  <td className="px-6 py-4 text-sm font-medium text-rose-600" data-id="element-410">
                    #{order.id}
                    {order.isCustomOrder && <span className="ml-2 inline-flex items-center" data-id="element-411">
                        <Sparkles className="w-3 h-3 text-orange-500" data-id="element-412" />
                      </span>}
                  </td>
                  <td className="px-6 py-4" data-id="element-413">
                    <div className="flex items-center" data-id="element-414">
                      <div className="h-8 w-8 rounded-full bg-rose-100 flex items-center justify-center text-rose-500 font-bold text-xs mr-3" data-id="element-415">
                        {order.customerName.charAt(0)}
                      </div>
                      <span className="text-sm font-medium text-gray-900" data-id="element-416">
                        {order.customerName}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-417">
                    {order.isCustomOrder ? <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800" data-id="element-418">
                        Custom
                      </span> : <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${order.userRegion === 'Limpopo' ? 'bg-blue-100 text-blue-800' : order.userRegion === 'Gauteng' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-419">
                        {order.userRegion || 'N/A'}
                      </span>}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500" data-id="element-420">
                    {order.items.length} items
                  </td>
                  <td className="px-6 py-4" data-id="element-421">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[order.status as keyof typeof statusStyles] || 'bg-gray-50 text-gray-600'}`} data-id="element-422">
                      <StatusIcon status={order.status} data-id="element-423" />
                      {order.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm font-bold text-gray-900" data-id="element-424">
                    {order.total > 0 ? formatZAR(order.total) : 'Pending Quote'}
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-425">
                    <Link to="/admin/orders" className="text-gray-400 hover:text-rose-500 transition-colors" data-id="element-426">
                      <MoreHorizontal size={18} data-id="element-427" />
                    </Link>
                  </td>
                </motion.tr>)}
            </tbody>
          </table>}
      </div>
    </motion.div>;
}